/**
 * CFB API client — isolated. Hits /cfb/* endpoints. Mirrors the NFL client: a failed
 * fetch returns an HONEST EMPTY shape (never fabricated rows), flagged `_sample:true`,
 * so the UI can show its own empty / "coming soon" state. No invented numbers render.
 */
const BASE = import.meta.env.VITE_API_URL || 'https://linebrkr-deploy-production.up.railway.app'

// Same global access-code gate as MLB/NFL. Reuse the stored code so /cfb board reads
// pass the gate; /cfb/status and /cfb/meta are public (no code needed).
const _hdrs = () => {
  try {
    const c = localStorage.getItem('brkr_access')
    return c ? { 'x-access-code': c } : {}
  } catch (_) { return {} }
}

async function tryGet(path, empty) {
  try {
    const r = await fetch(`${BASE}${path}`, { headers: _hdrs() })
    if (r.ok) return await r.json()
    return { ...(empty || {}), _sample: true, _status: r.status }
  } catch (_) {
    return { ...(empty || {}), _sample: true }
  }
}

export const getCFBStatus = (season) =>
  tryGet(`/cfb/status${season ? `?season=${season}` : ''}`,
    { sport: 'cfb', live: false, phase: 'coming_soon', counts: {} })

export const getCFBMeta = () =>
  tryGet('/cfb/meta', { sport: 'cfb', prop_types: [], positions: [] })

export const getCFBLeaderboard = (season = 2025, week = 1, propType = '', limit = 50, sort = 'prob', search = '', position = '', matchup = '', day = '') =>
  tryGet(`/cfb/leaderboard?season=${season}&week=${week}&prop_type=${encodeURIComponent(propType)}&limit=${limit}&sort=${sort}${search ? `&search=${encodeURIComponent(search)}` : ''}${position ? `&position=${encodeURIComponent(position)}` : ''}${matchup ? `&matchup=${encodeURIComponent(matchup)}` : ''}${day ? `&day=${encodeURIComponent(day)}` : ''}`,
    { season, week, phase: 'coming_soon', plays: [], total: 0, days: [] })

export const getCFBScorecard = (season = 2025, markets = '', minConf = 0, minTier = '') =>
  tryGet(`/cfb/scorecard?season=${season}${markets ? `&markets=${encodeURIComponent(markets)}` : ''}${minConf ? `&min_conf=${minConf}` : ''}${minTier ? `&min_tier=${encodeURIComponent(minTier)}` : ''}`,
    { season, overall: {}, by_prop: {}, by_confidence: {} })

export const getCFBResults = (season = 2025, week = 0) =>
  tryGet(`/cfb/results?season=${season}&week=${week}`, { season, week, plays: [] })

export const getCFBValidation = (seasons = '') =>
  tryGet(`/cfb/validation${seasons ? `?seasons=${seasons}` : ''}`,
    { seasons: [], overall: {}, markets: {}, summary: { pass: [], review: [], building: [] } })

export const getCFBTop = (season = 2026, week = 1, limit = 12) =>
  tryGet(`/cfb/top?season=${season}&week=${week}&limit=${limit}`,
    { season, week, phase: 'coming_soon', top: [], value: [], by_market: [], td_outlook: 0, counts: {} })

export const getCFBGames = (season = 2026, week = 1) =>
  tryGet(`/cfb/games?season=${season}&week=${week}`,
    { season, week, phase: 'coming_soon', games: [], days: [] })

export const getCFBSignals = (season = 2026, week = 1) =>
  tryGet(`/cfb/signals?season=${season}&week=${week}`,
    { season, week, phase: 'coming_soon', td_storm: [], shootout: null, stack: null, air_raid: [], ground_control: [] })

export const getCFBCheatsheet = (season = 2026, week = 1) =>
  tryGet(`/cfb/cheatsheet?season=${season}&week=${week}`,
    { season, week, phase: 'coming_soon', markets: {}, market_labels: {}, value: [] })

export const getCFBPlayer = (cfbdId, season = 2026, week = 1) =>
  tryGet(`/cfb/player?cfbd_id=${encodeURIComponent(cfbdId)}&season=${season}&week=${week}`,
    { player: {}, plays: [], recent: [] })
